/**
 * @description Configures axios with interceptors for token injection and token refreshing upon 401 Unauthorized errors.
 */

import axios from 'axios';
import { toast } from 'sonner';
import { translateMessage } from './translationResolver';
import { API_BASE_URL } from '@/services/apiBase';
import { clearAccessToken, getAccessToken, setAccessToken } from './accessTokenStore';

export const API_URL = API_BASE_URL.endsWith('/api')
  ? API_BASE_URL
  : `${API_BASE_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

type RefreshResponse = {
  accessToken?: string;
  user?: unknown;
};

let refreshRequest: Promise<RefreshResponse> | null = null;
const TOAST_DEDUPE_WINDOW_MS = 5000;
const recentToastTimestamps = new Map<string, number>();

const clearClientSession = () => {
  clearAccessToken();
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
};

const showDeduplicatedErrorToast = (toastKey: string, message: string) => {
  const now = Date.now();
  const previous = recentToastTimestamps.get(toastKey) ?? 0;
  if (now - previous < TOAST_DEDUPE_WINDOW_MS) {
    return;
  }
  recentToastTimestamps.set(toastKey, now);
  toast.error(message);
};

const isRefreshAuthFailure = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403;
};

const applyRefreshedSession = (payload: RefreshResponse): string => {
  const accessToken =
    typeof payload?.accessToken === 'string' ? payload.accessToken.trim() : '';
  if (!accessToken) {
    throw new Error('Missing refreshed access token');
  }
  setAccessToken(accessToken);
  localStorage.removeItem('refreshToken');
  if (payload.user) {
    localStorage.setItem('user', JSON.stringify(payload.user));
  }
  return accessToken;
};

const refreshAccessToken = async (): Promise<RefreshResponse> => {
  if (!refreshRequest) {
    refreshRequest = axios
      .post<RefreshResponse>(
        `${API_URL}/auth/refresh-token`,
        {},
        { withCredentials: true }
      )
      .then((res) => res.data ?? {})
      .finally(() => {
        refreshRequest = null;
      });
  }
  return refreshRequest;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Aborted/canceled requests are expected during fast UI transitions.
    // They should not trigger global error toasts.
    if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const requestUrl = String(originalRequest?.url ?? '');
    const isRefreshCall = requestUrl.includes('/auth/refresh-token');

    if (error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !requestUrl.includes('/login') &&
      !isRefreshCall
    ) {
      originalRequest._retry = true;

      try {
        const refreshPayload = await refreshAccessToken();
        const accessToken = applyRefreshedSession(refreshPayload);

        originalRequest.headers = {
          ...(originalRequest.headers ?? {}),
          Authorization: `Bearer ${accessToken}`,
        };

        return api(originalRequest);

      } catch (refreshError) {
        if (isRefreshAuthFailure(refreshError)) {
          clearClientSession();
          window.location.href = '/login';
        }

        return Promise.reject(error);
      }
    }

    if (error.response) {
      const status = error.response.status;

      const serverMessage = error.response.data?.message
      if (serverMessage) {
        showDeduplicatedErrorToast(
          `message:${serverMessage}`,
          translateMessage(serverMessage)
        );
      }
      //fallback
      else {
        switch (status) {
          case 400:
            showDeduplicatedErrorToast("status:400", translateMessage("common.invalidRequest"));
            break;
          case 401:
            if (!requestUrl.includes('/auth/login')) {
               showDeduplicatedErrorToast("status:401", translateMessage("common.sessionExpired"));
            }
            break;
          case 403:
            showDeduplicatedErrorToast("status:403", translateMessage("common.accessDenied"));
            break;
          case 404:
            showDeduplicatedErrorToast("status:404", translateMessage("common.notFound"));
            break;
          case 429:
            showDeduplicatedErrorToast("status:429", translateMessage("common.tooManyRequests"));
            break;
          case 500:
            showDeduplicatedErrorToast("status:500", translateMessage("common.serverError"));
            break;
          case 503:
            showDeduplicatedErrorToast(
              "status:503",
              translateMessage("common.serviceStarting")
            );
            break;
          default:
            showDeduplicatedErrorToast(
              `status:${status}`,
              translateMessage("common.errorWithStatus").replace(
                "{status}",
                String(status)
              )
            );
        }
      }
    } else if (error.request) {
      showDeduplicatedErrorToast("network:no-connection", translateMessage("common.noConnection"));
    } else {
      showDeduplicatedErrorToast("client:unexpected", translateMessage("common.unexpectedError"));
    }

    return Promise.reject(error);
  }
);

export default api;
